import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Compass, Rocket } from "lucide-react";
import { Reveal } from "./Reveal";

const STEPS = [
  { n: "01", title: "DISCOVER", desc: "Understand the brand, audience and objectives." },
  { n: "02", title: "STRATEGIZE", desc: "Build the creative and growth direction." },
  { n: "03", title: "CREATE", desc: "Produce the brand assets, content and digital experiences." },
  { n: "04", title: "LAUNCH", desc: "Take the campaign or digital experience live." },
  { n: "05", title: "GROW", desc: "Analyze, optimize and continuously improve." },
];

const DiscoverVisual = () => (
  <div className="relative w-full h-full flex items-center justify-center">
    {[0, 1, 2].map((i) => (
      <motion.span
        key={i}
        aria-hidden="true"
        className="absolute rounded-full border border-[#8B2CFF]/30"
        style={{ width: 130 + i * 70, height: 130 + i * 70 }}
        animate={{ opacity: [0.25, 0.65, 0.25] }}
        transition={{ duration: 3, repeat: Infinity, delay: i * 0.5, ease: "easeInOut" }}
      />
    ))}
    <motion.div
      aria-hidden="true"
      className="absolute w-[270px] h-[270px] rounded-full"
      style={{ background: "conic-gradient(from 0deg, rgba(196,60,255,0.4), transparent 70deg)" }}
      animate={{ rotate: 360 }}
      transition={{ duration: 4.5, repeat: Infinity, ease: "linear" }}
    />
    <div className="relative w-16 h-16 rounded-full bg-[#0a0a10] border border-[#8B2CFF]/50 flex items-center justify-center shadow-[0_0_30px_-6px_rgba(139,44,255,0.7)]">
      <Search className="w-6 h-6 text-[#C43CFF]" strokeWidth={1.5} />
    </div>
  </div>
);

const STRAT_NODES = [
  { x: 160, y: 150 },
  { x: 70, y: 80 },
  { x: 250, y: 70 },
  { x: 80, y: 240 },
  { x: 255, y: 225 },
];

const StrategizeVisual = () => (
  <div className="w-full h-full flex items-center justify-center">
    <svg viewBox="0 0 320 300" className="w-[300px] h-[280px]" aria-hidden="true">
      <defs>
        <linearGradient id="pg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#6D00FF" />
          <stop offset="1" stopColor="#C43CFF" />
        </linearGradient>
      </defs>
      {STRAT_NODES.slice(1).map((n, i) => (
        <motion.line
          key={i}
          x1="160"
          y1="150"
          x2={n.x}
          y2={n.y}
          stroke="url(#pg)"
          strokeWidth="1"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 1.1, delay: 0.3 + i * 0.2 }}
        />
      ))}
      <motion.line x1="70" y1="80" x2="250" y2="70" stroke="url(#pg)" strokeWidth="1" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1, delay: 1.2 }} />
      <motion.line x1="80" y1="240" x2="255" y2="225" stroke="url(#pg)" strokeWidth="1" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1, delay: 1.4 }} />
      {STRAT_NODES.map((n, i) => (
        <motion.circle
          key={i}
          cx={n.x}
          cy={n.y}
          r={i === 0 ? 10 : 6}
          fill="#0a0a10"
          stroke="#C43CFF"
          strokeWidth="1.5"
          style={{ transformOrigin: `${n.x}px ${n.y}px` }}
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: i * 0.15, duration: 0.5 }}
        />
      ))}
    </svg>
  </div>
);

const CreateVisual = () => (
  <div className="w-full h-full flex items-center justify-center gap-4 px-8">
    {["/images/work-1.webp", "/images/work-2.webp", "/images/work-3.webp"].map((src, i) => (
      <motion.div
        key={src}
        className="w-28 sm:w-36 aspect-[4/3] rounded-md overflow-hidden border border-white/15 shadow-[0_0_30px_-10px_rgba(109,0,255,0.6)]"
        style={{ rotate: (i - 1) * 6 }}
        animate={{ y: [(i - 1) * 10, (i - 1) * 10 - 8, (i - 1) * 10] }}
        transition={{ duration: 3.2 + i * 0.4, repeat: Infinity, ease: "easeInOut" }}
      >
        <img src={src} alt="" className="w-full h-full object-cover" />
      </motion.div>
    ))}
  </div>
);

const LaunchVisual = () => (
  <div className="w-full h-full flex items-center justify-center px-10">
    <div className="w-full max-w-xs rounded-lg border border-white/15 bg-[#0a0a10] overflow-hidden shadow-[0_0_40px_-12px_rgba(109,0,255,0.5)]">
      <div className="flex items-center gap-1.5 px-4 py-3 border-b border-white/[0.08]">
        {[0, 1, 2].map((i) => (
          <span key={i} className="w-2 h-2 rounded-full bg-white/15" />
        ))}
        <span className="ml-3 text-[9px] uppercase tracking-[0.2em] text-[#A6A6B0]/60">
          brandzenith.studio
        </span>
      </div>
      <div className="p-6 flex flex-col items-center gap-4">
        <motion.div
          animate={{ y: [6, -14, 6], opacity: [0.7, 1, 0.7] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
        >
          <Rocket className="w-8 h-8 text-[#C43CFF]" strokeWidth={1.5} />
        </motion.div>
        <div className="w-full h-1 rounded-full bg-white/[0.08] overflow-hidden">
          <motion.div
            className="h-full bg-gradient-to-r from-[#6D00FF] to-[#C43CFF] origin-left"
            animate={{ scaleX: [0, 1] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>
        <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#E8B6FF] border border-[#8B2CFF]/40 rounded px-3 py-1 animate-pulse-glow">
          Live
        </span>
      </div>
    </div>
  </div>
);

const GrowVisual = () => (
  <div className="w-full h-full flex items-center justify-center px-10">
    <svg viewBox="0 0 320 220" className="w-full max-w-sm" aria-hidden="true">
      <defs>
        <linearGradient id="gg" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#6D00FF" />
          <stop offset="1" stopColor="#C43CFF" />
        </linearGradient>
        <linearGradient id="ga" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="rgba(139,44,255,0.3)" />
          <stop offset="1" stopColor="rgba(139,44,255,0)" />
        </linearGradient>
      </defs>
      {[50, 100, 150].map((y) => (
        <line key={y} x1="10" y1={y} x2="310" y2={y} stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
      ))}
      <motion.path
        d="M10 190 L75 150 L140 165 L205 100 L265 75 L310 30 L310 210 L10 210 Z"
        fill="url(#ga)"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 1 }}
      />
      <motion.path
        d="M10 190 L75 150 L140 165 L205 100 L265 75 L310 30"
        fill="none"
        stroke="url(#gg)"
        strokeWidth="2.5"
        strokeLinecap="round"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 1.8, ease: "easeInOut" }}
      />
      <motion.circle
        cx="310"
        cy="30"
        r="5"
        fill="#E8B6FF"
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 1, 0.6, 1] }}
        transition={{ delay: 1.7, duration: 1.4, repeat: Infinity }}
      />
    </svg>
  </div>
);

const VISUALS = [DiscoverVisual, StrategizeVisual, CreateVisual, LaunchVisual, GrowVisual];

const StepVisual = ({ active }) => {
  const Visual = VISUALS[active];
  return (
    <div
      data-testid="process-visual"
      className="relative h-[340px] lg:h-[440px] glass-card rounded-lg overflow-hidden"
    >
      <div className="absolute inset-0 grid-bg opacity-50" aria-hidden="true" />
      <AnimatePresence mode="wait">
        <motion.div
          key={active}
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 1.04 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-0"
        >
          <Visual />
        </motion.div>
      </AnimatePresence>
      <div className="absolute bottom-5 left-6 font-display text-xs font-bold uppercase tracking-[0.24em] text-[#C43CFF]">
        {STEPS[active].n} — {STEPS[active].title}
      </div>
    </div>
  );
};

export const Process = () => {
  const [active, setActive] = useState(0);

  return (
    <section data-testid="process-section" className="relative py-28 lg:py-40">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-2xl mb-16 lg:mb-24">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#8B2CFF] mb-6">
              The Process
            </p>
            <h2 className="font-display text-3xl sm:text-5xl font-extrabold uppercase tracking-tight leading-[1.05]">
              From idea <span className="text-gradient-purple">to impact.</span>
            </h2>
          </Reveal>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          <div className="hidden lg:block sticky top-28">
            <StepVisual active={active} />
          </div>
          <div>
            {STEPS.map((s, i) => (
              <motion.div
                key={s.n}
                data-testid={`process-step-${i}`}
                onViewportEnter={() => setActive(i)}
                viewport={{ margin: "-45% 0px -45% 0px" }}
                className="group py-12 lg:py-16 border-b border-white/[0.06] first:border-t"
              >
                <div className="flex items-baseline gap-6 sm:gap-8">
                  <span
                    className={`font-display text-5xl sm:text-6xl font-extrabold tracking-tighter transition-colors duration-700 ${
                      active === i ? "text-gradient-purple" : "text-white/10"
                    }`}
                  >
                    {s.n}
                  </span>
                  <div>
                    <h3 className="font-display text-xl sm:text-2xl font-extrabold uppercase tracking-[0.12em] mb-3">
                      {s.title}
                    </h3>
                    <p className="text-sm sm:text-base text-[#A6A6B0] leading-relaxed max-w-md">
                      {s.desc}
                    </p>
                  </div>
                </div>
                <div className="lg:hidden mt-8">
                  <StepVisual active={i} />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
